// AsyncSearchMultiSelectInput.tsx
import React, { useEffect, useState, useCallback } from 'react';
import {
    Autocomplete,
    FormControl,
    Grid,
    TextField,
    CircularProgress,
} from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LabelInput from './LabelInput';
import { useCustomTheme } from '../../../contexts/ThemeContext';

interface Item {
    id: string;
    value: string | number;
    name: string;
}

interface AsyncSearchMultiSelectInputProps {
    id: string;
    label: string;
    value: string[];
    fetchOptions: (search: string) => Promise<Item[]>; // async fetch function
    fetchSelected?: (ids: string[]) => Promise<Item[]>; // optional: fetch initial selected items by ID
    handleChange: (field: string, value: any[]) => void;
    handleBlur: (e: React.FocusEvent<any>) => void;
    hasSubmitted: boolean;
    errors?: string[] | string;
    touched?: boolean;
    enableLabel?: boolean;
    isMobile?: boolean;
    caption?: string;
    debounceMs?: number;
}

const AsyncSearchMultiSelectInput: React.FC<
    AsyncSearchMultiSelectInputProps
> = ({
    caption,
    id,
    label,
    value,
    fetchOptions,
    fetchSelected,
    hasSubmitted,
    handleBlur,
    handleChange,
    enableLabel,
    isMobile,
    debounceMs = 300,
}) => {
    const { theme } = useCustomTheme();
    const labelId = `${id}-label`;

    const [savedValues, setSavedValues] = useState<Item[]>([]);
    const [options, setOptions] = useState<Item[]>([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');

    // Load initial selected values by ID on mount (if fetchSelected provided)
    useEffect(() => {
        if (value.length === 0) return;

        if (fetchSelected) {
            fetchSelected(value).then((items) => setSavedValues(items));
        }
    }, []); // only on mount

    // Debounced search
    useEffect(() => {
        if (inputValue.trim() === '') {
            setOptions([]);
            return;
        }

        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const results = await fetchOptions(inputValue);
                // Filter out already selected items
                const filtered = results.filter(
                    (r) => !savedValues.some((s) => s.id === r.id)
                );
                setOptions(filtered);
            } catch (err) {
                console.error('Failed to fetch options', err);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        }, debounceMs);

        return () => clearTimeout(timer);
    }, [inputValue]);

    const handleSelectChange = (newValue: Item[]) => {
        setSavedValues(newValue);
        handleChange(
            id,
            newValue.map((item) => item.id)
        );
    };

    const isValueSelected = savedValues.length > 0;

    return (
        <FormControl fullWidth margin="normal">
            <Box display="flex" alignItems="center">
                <Grid
                    container
                    alignItems={isMobile ? 'flex-start' : 'center'}
                    spacing={2}
                >
                    {!enableLabel ? (
                        <LabelInput
                            label={label}
                            enableLabel={enableLabel}
                            isMobile={isMobile}
                            caption={caption}
                        />
                    ) : null}
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={isMobile ? 12 : enableLabel ? 12 : 9.5}
                        lg={enableLabel ? 12 : 9.8}
                        xl={enableLabel ? 12 : 10.25}
                    >
                        <Autocomplete
                            multiple
                            id={labelId}
                            options={options}
                            loading={loading}
                            onBlur={handleBlur}
                            disabled={hasSubmitted}
                            getOptionLabel={(option) => option.name}
                            filterOptions={(x) => x} // disable built-in filtering, we handle it server-side
                            inputValue={inputValue}
                            onInputChange={(_, newInputValue) =>
                                setInputValue(newInputValue)
                            }
                            onChange={(_, selectedValues) =>
                                handleSelectChange(selectedValues)
                            }
                            value={savedValues}
                            isOptionEqualToValue={(option, val) =>
                                option.id === val.id
                            }
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const { key, ...tagProps } = getTagProps({
                                        index,
                                    });
                                    return (
                                        <Chip
                                            sx={{
                                                backgroundColor:
                                                    theme.palette.primary.main,
                                                color: theme.palette.text
                                                    .primary,
                                            }}
                                            key={key}
                                            label={option.name}
                                            {...tagProps}
                                        />
                                    );
                                })
                            }
                            renderInput={(params) => (
                                <TextField
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            color: isValueSelected
                                                ? theme.palette.text.secondary
                                                : theme.palette.text.disabled,
                                            '&.Mui-focused': {
                                                color: theme.palette.primary
                                                    .main,
                                            },
                                        },
                                    }}
                                    {...params}
                                    label={enableLabel ? label : undefined}
                                    placeholder="Start typing to search..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loading ? (
                                                    <CircularProgress
                                                        color="inherit"
                                                        size={18}
                                                    />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </Box>
        </FormControl>
    );
};

export default AsyncSearchMultiSelectInput;
